import { io, Socket } from 'socket.io-client';
import { supabase } from '../../libs/supabaseClient';

const URL = process.env.REACT_APP_CHATBOT_URL || 'http://localhost:3005';
const NSP = `${URL}/support`;

export interface SupportMessageEvent {
  supportId: string;
  from: 'user' | 'agent';
  fromName: string;
  text: string;
  ts: string;
}

/**
 * Cliente socket.io para el namespace `/support` (handoff a humano).
 *
 * Resuelve el token JWT de Supabase al conectar y lo envía en `auth.token`.
 * Si no hay sesión (visitor), envía `token: null` — el server lo trata como
 * visitor y le exige visitorName/visitorEmail al emitir `handoff:request`.
 *
 * Mismo host que el server Express de aiChatBot (:3005 / ngrok), ruta
 * `/socket.io/` servida por socket.io al attach del httpServer.
 */
class SupportSocketManager {
  private socket: Socket | null = null;
  private connectingPromise: Promise<Socket> | null = null;

  /**
   * Idempotente: si ya existe un socket (conectándose o conectado) o hay una
   * conexión en curso, devuelve ESE. Nunca crea una segunda instancia.
   *
   * Esto es crítico: connectSupportSocket se llama desde el effect de recovery
   * Y desde startHandoff, y React StrictMode duplica el mount. Si cada llamada
   * creara su propio socket, todos se unarían a la sala del hilo y registrarían
   * sus propios listeners → cada evento del server dispararía N handlers y
   * veríamos mensajes duplicados/triplicados. Reusar una sola instancia lo evita.
   */
  async connect(): Promise<Socket> {
    if (this.socket) return this.socket;
    if (this.connectingPromise) return this.connectingPromise;
    this.connectingPromise = (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      this.socket = io(NSP, {
        transports: ['websocket', 'polling'],
        auth: { token: token || null },
        extraHeaders: { 'ngrok-skip-browser-warning': 'true' } as any,
      });
      return this.socket;
    })();
    try {
      return await this.connectingPromise;
    } finally {
      this.connectingPromise = null;
    }
  }

  get(): Socket | null {
    return this.socket;
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.connectingPromise = null;
  }
}

export const supportSocket = new SupportSocketManager();
